import { TaskStep, TestData } from "graphql/types"

// Formats an ISO date string into a human-readable date and time string in the "de-DE" locale.
// Usage: formatDateTime("2023-10-01T12:34:56Z") -> "01.10.2023, 12:34"
export const formatDateTime = (isoString: string) => {
  const date = new Date(isoString)

  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date)
}

// Formats a total number of seconds into a string representing hours, minutes, and seconds.
// Usage: formatTime(3661) -> "1:01:01"
// Usage: formatTime(90061) -> "25:01:01" (shows actual hours, not days)
export const formatTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
  } else if (minutes > 0) {
    return `${minutes}:${String(seconds).padStart(2, "0")}`
  } else {
    return `0:${String(seconds).padStart(2, "0")}`
  }
}

// Calculates the difference between two date-time strings and returns the difference in hours and minutes.
// Usage: getTimeDifference("2023-10-01T12:00:00Z", "2023-10-01T14:30:00Z") -> { hours: 2, minutes: 30 }
export const getTimeDifference = (beginTime: string, endTime: string) => {
  if (!beginTime || !endTime) {
    return { hours: 0, minutes: 0 }
  }
  
  const start = new Date(beginTime)
  const end = new Date(endTime)

  // Check if dates are valid
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { hours: 0, minutes: 0 }
  }

  const diffMs = end.getTime() - start.getTime()
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

  return {
    hours,
    minutes,
  }
}

// Formats the time difference between two date-time strings into a human-readable string.
// Usage: getTimeDifferencFormated("2023-10-01T12:00:00Z", "2023-10-01T14:30:00Z") -> "2h 30min"
export const getTimeDifferencFormated = (
  beginTime: string,
  endTime: string
) => {
  const { hours, minutes } = getTimeDifference(beginTime, endTime)
  return `${hours}h ${minutes}min`
}

// Calculates the total time in seconds from an array of TestData objects.
// Usage: calculateTotalTimeInSeconds(testDataArray) -> 7200
export const calculateTotalTimeInSeconds = (data: TestData[]): number => {
  if (!data || !Array.isArray(data)) {
    return 0
  }
  
  return data.reduce((total, { beginTime, endTime, type, helper }) => {
    if (beginTime && endTime) {
      const start = new Date(beginTime).getTime()
      const end = new Date(endTime).getTime()
      
      // Check if dates are valid
      if (isNaN(start) || isNaN(end)) {
        return total
      }
      
      const diffInSeconds =
        type === TaskStep.HELPER
          ? ((end - start) / 1000) * (helper?.length || 1)
          : (end - start) / 1000

      return total + diffInSeconds
    }
    return total
  }, 0)
}
